import { useAppStore } from "@/stores/app";
import { useUIStore } from "@/stores/ui";

import { getHeaders } from "./common";
import { processErrors } from "./errors";

class RestClient {
    constructor(baseUrl = undefined) {
        if (!baseUrl) {
            this.baseUrl = `${import.meta.env.VITE_APP_BACKEND}/internal`;
        } else {
            this.baseUrl = baseUrl;
        }
    }

    async _request({ method, resource, body, overlay, filename, noCache }) {
        let uiStore;
        const appStore = useAppStore();

        const showOverlay = overlay === true || overlay === undefined;

        if (showOverlay) {
            uiStore = useUIStore();
            uiStore.startWorking();
        }
        try {
            // Populate headers
            const headers = getHeaders(noCache, filename);

            // Populate options
            const options = {
                method,
                headers,
            };

            // Populate body
            if (body) {
                options.body = JSON.stringify(body);
            }

            // Make request
            let response;
            try {
                response = await fetch(`${this.baseUrl}${resource}`, options);
                appStore.connected = true;
            } catch (_) {
                appStore.connected = false;
                return;
            }
            const status = response.status;

            // Process response

            // Check for success
            if (status === 200) {
                if (filename) {
                    const blob = await response.blob();

                    const url = window.URL.createObjectURL(new Blob([blob]));
                    const link = document.createElement("a");

                    link.href = url;
                    link.setAttribute("download", filename ? filename : "unknown");
                    document.body.appendChild(link);
                    link.click();

                    return null;
                }
                return await response.json();
            } else if (status === 204) {
                return null;
            }

            const error = await response.json();
            processErrors(status, error);
        } finally {
            if (showOverlay) {
                uiStore.finishWorking();
            }
        }
    }

    async get(resource, params = undefined, options = {}) {
        let res = resource;
        if (params) {
            const urlParams = new URLSearchParams(params).toString();
            res = `${resource}?${urlParams}`;
        }

        return await this._request({
            method: "GET",
            resource: res,
            body: undefined,
            overlay: options.overlay,
            filename: options.filename,
            noCache: options.noCache || false,
        });
    }

    async post(resource, body, options = {}) {
        return await this._request({
            method: "POST",
            resource: resource,
            body: body,
            overlay: options.overlay,
            filename: undefined,
            noCache: options.noCache || false,
        });
    }

    async put(resource, body, options = {}) {
        return await this._request({
            method: "PUT",
            resource: resource,
            body: body,
            overlay: options.overlay,
            filename: undefined,
            noCache: options.noCache || false,
        });
    }

    async patch(resource, body, options = {}) {
        return await this._request({
            method: "PATCH",
            resource: resource,
            body: body,
            overlay: options.overlay,
            filename: undefined,
            noCache: options.noCache || false,
        });
    }

    async delete(resource, body, options = {}) {
        return await this._request({
            method: "DELETE",
            resource: resource,
            body: body,
            overlay: options.overlay,
            filename: undefined,
            noCache: options.noCache || false,
        });
    }

    async download(resource, filename, options = { overlay: true }) {
        return await this._request({
            method: "GET",
            resource: resource,
            body: undefined,
            overlay: options.overlay,
            filename: filename,
            noCache: options.noCache || false,
        });
    }

    async upload(resource, file, options = { overlay: true }) {
        let uiStore;

        if (options.overlay) {
            uiStore = useUIStore();
            uiStore.startWorking();
        }

        const form = new FormData();
        form.append("file", file);

        const headers = getHeaders();
        delete headers["Content-Type"];

        try {
            const response = await fetch(`${this.baseUrl}${resource}`, {
                method: "POST",
                body: form,
                headers,
            });

            const status = response.status;
            const json = await response.json();

            if (status === 200) {
                return json;
            }

            processErrors(status, json);
        } finally {
            if (options.overlay) {
                uiStore.finishWorking();
            }
        }
    }
}

export default RestClient;
